import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";

import { RoutePaths } from "~/common/constants/route-paths";
import { useShallowCurrentUserStore } from "~/common/global-states/current-user-store";

const routesToSkipForAuthorised: string[] = [
  RoutePaths.SIGN_IN,
  RoutePaths.SIGN_UP,
  RoutePaths.PASSWORD_RECOVERY,
];

const authRequiredRoutes: string[] = [
  RoutePaths.APPLICATIONS,
  RoutePaths.COMPANY,
  RoutePaths.ADMIN,
  RoutePaths.USER,
  RoutePaths.SETTINGS,
  RoutePaths.SUPPORT,
];

const routesToSkipUserLoad: string[] = [
  RoutePaths.LOGIN_COMPANY_INVITED_EXISTING,
  RoutePaths.LOGIN_COMPANY_INVITED_NEW,
];

/**
 * A wrapper component that handles authentication logic, including loading services and user data,
 * and redirects authenticated users away from specific routes.
 *
 * @param {Object} props - The props object passed to the AuthWrapper component.
 * @param {Route.ComponentProps} props.loaderData - The data loaded for the route, including variables required for initializing services.
 * @return {JSX.Element} Returns the child route components wrapped by the authentication logic.
 */
export default function AuthRoutesWrapper() {
  const { loadUser, user, isLoaded } = useShallowCurrentUserStore(
    ({ user, loadUser, isLoaded }) => ({
      user,
      loadUser,
      isLoaded,
    }),
  );
  const location = useLocation();
  const navigate = useNavigate();

  const normalizedPathName = location.pathname.endsWith("/")
    ? location.pathname.slice(0, -1)
    : location.pathname;

  // Load services and user on mount
  useEffect(() => {
    if (!routesToSkipUserLoad.includes(normalizedPathName) && !isLoaded) {
      loadUser();
    }
    // Re-evaluate on path changes to recover from initial invite pages
  }, [normalizedPathName, isLoaded, loadUser]);

  // Redirect to dashboard if user is logged in and trying to access sign-in/sign-up page/
  useEffect(() => {
    // In production build it may add '/' to the end of the path, so we need to remove it

    if (!isLoaded) {
      return;
    }

    if (user && routesToSkipForAuthorised.includes(normalizedPathName)) {
      navigate(RoutePaths.APPLICATIONS);
    }

    if (!user && authRequiredRoutes.includes(normalizedPathName)) {
      navigate(RoutePaths.SIGN_IN);
    }
  }, [user, isLoaded, location.pathname]);

  return <Outlet />;
}
